import Button from '../../../components/base/Button';

const ProductShowcase = () => {
  const products = [
    {
      id: 1,
      title: "Phishing Protection",
      problem: "65% of cyber attacks start with phishing",
      solution: "Test your team before hackers do",
      description: "Company-wide phishing simulation + 2-week free trial of 24/7 Managed Cyber Protection",
      image: "https://static.readdy.ai/image/807f7a13fa3a4707437af02957aa93c9/45d9a998ef2d8c1fc747fae2da1161f7.png",
      price: "$100",
      originalPrice: null,
      badge: "Best Value",
      features: ["Company-wide phishing test", "2-week protection trial", "Vulnerability report"],
      ctaText: "Get Simulation",
      popular: true,
      link: "/phishing-simulation"
    },
    {
      id: 4,
      title: "24/7 Protection",
      problem: "Threats don't sleep - neither should your security",
      solution: "Round-the-clock monitoring & instant response",
      description: "Complete managed security",
      image: "https://static.readdy.ai/image/807f7a13fa3a4707437af02957aa93c9/94d0f71b6ceceeac4b6e7dd2fdc9f1fe.png",
      price: "$20/mo",
      originalPrice: null,
      badge: "Per User",
      features: ["24/7 monitoring", "Instant response", "Monthly reports"],
      ctaText: "Get Protected",
      popular: false,
      volumeDiscount: true,
      link: "/managed-services"
    },
    {
      id: 3,
      title: "Risk Assessment",
      problem: "State examiners require recent security reports",
      solution: "Get examination-ready in 48 hours",
      description: "Know your vulnerabilities instantly",
      image: "https://static.readdy.ai/image/807f7a13fa3a4707437af02957aa93c9/963c7c9b418cb06d34049bee49f33662.png",
      price: "$249",
      originalPrice: "$499",
      badge: "Upcoming Exam?",
      features: ["Full audit", "Action plan", "Compliance check"],
      ctaText: "Get Assessment",
      popular: false,
      link: "/risk-assessment"
    },
    {
      id: 2,
      title: "Policies & Procedures",
      problem: "Missing policies = compliance failures",
      solution: "36+ ready-to-use security policies",
      description: "Complete security policy manual",
      image: "https://static.readdy.ai/image/807f7a13fa3a4707437af02957aa93c9/0dac560c206b7322f1a35800d4acd300.png",
      price: "$395",
      originalPrice: null,
      badge: "Foundation",
      features: ["36+ policies included", "Fully customizable", "GLBA compliant"],
      ctaText: "Get Policy Manual",
      popular: false,
      link: "/security-policies-manual"
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            We Understand Your Challenges
          </h2>
          <p className="text-lg text-gray-600">
            Real problems. Proven solutions. Immediate results.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-product-shop>
          {products.map((product) => (
            <div key={product.id} className={`bg-white rounded-xl shadow-lg border-2 overflow-hidden hover:shadow-xl transition-all duration-300 ${product.popular ? 'border-red-500 transform scale-105' : 'border-gray-200'}`}>
              {/* Product Badge */}
              <div className={`text-white text-sm font-bold px-4 py-2 text-center ${product.popular ? 'bg-red-600' : 'bg-gray-700'}`}>
                {product.badge}
              </div>
              
              {/* Product Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={product.image} 
                  alt={product.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Product Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{product.title}</h3>
                
                {/* Problem Statement */}
                <div className="mb-3 pb-3 border-b border-gray-200">
                  <div className="flex items-start gap-2">
                    <i className="ri-alert-line text-red-500 text-lg mt-0.5 flex-shrink-0"></i>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">The Problem</p>
                      <p className="text-sm font-medium text-gray-800">{product.problem}</p>
                    </div>
                  </div>
                </div>

                {/* Solution Statement */}
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <div className="flex items-start gap-2">
                    <i className="ri-lightbulb-line text-green-500 text-lg mt-0.5 flex-shrink-0"></i>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Our Solution</p>
                      <p className="text-sm font-medium text-gray-800">{product.solution}</p>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-1 mb-4">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm">
                      <i className="ri-check-line text-green-500"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Pricing */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl font-bold text-gray-900">{product.price}</span>
                  {product.originalPrice && (
                    <span className="text-lg text-gray-500 line-through">{product.originalPrice}</span>
                  )}
                </div>

                {/* Volume Discount Note */}
                {product.volumeDiscount && (
                  <p className="text-xs text-gray-500 mb-4">
                    Volume discounts available for 20+ users
                  </p>
                )}

                {/* CTA Button */}
                <Button 
                  variant={product.popular ? "primary" : "secondary"}
                  size="sm"
                  className="w-full py-3 font-bold whitespace-nowrap"
                  href={product.link}
                >
                  {product.ctaText}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center bg-gray-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            Have a Unique Challenge?
          </h3>
          <p className="text-gray-600 mb-6">
            Let's discuss your specific security needs - we'll create a custom solution
          </p>
          <Button variant="outline" size="lg" className="px-8 py-4 font-bold whitespace-nowrap" href="/schedule-call">
            Schedule Call
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
